/**
 * Container Widget Component
 *
 * This JavaScript code assiciates with both the Responsive Grid & the Simple AEM container
 * components.
 *
 * @see /aem/apps/jcr_root/apps/aweb/components/content/container/simple.html
 * @see /aem/apps/jcr_root/apps/aweb/components/content/container/responsiveGrid.html
 */

/**
 * Background Styling
 *
 * AEM is wrapping the component's HTL in an additional `<div>` that gets all of the grid
 * classes (columns, rows, etc). For our background styles we want to add an additional attribute
 * (`style`) to that parent element.
 *
 * @param {Node} domEl - the root DOM node from the HTL
 */
import utils from 'utils';
import ANALYTICS from 'constants/analytics';

const applyBackgroundStyleToParent = (domEl) => {
  const parent = domEl.parentElement;
  const backgroundStyle = domEl.dataset.parentBackgroundStyle; // "data-parent-background-style"

  parent && backgroundStyle && parent.setAttribute('style', backgroundStyle);
};

/**
 * Hide the container
 * @param $domEl the DOM element to hide.
 */
const hideContainer = ($domEl) => {
  // Add proper "hide" attributes/classes
  $domEl.classList.add('hide-dismissable');
  $domEl.setAttribute('aria-hidden', 'true');
}

/**
 * Dismissable Container
 *
 * If the content author has selected that this specific AEM container is "dismissable"
 * the root HTL DOM element will have a `data-dismissable` attribute added to it. We
 * use this to hook into the DOM and add a button that when clicked will hide the dismissable
 * UI.
 *
 * Here is what the mark up should look like
 *
 * <div class="container">
 *   <div id="{AEM Configured ID}" data-dismissable>
 *     <div class="dismissable-container__button-wrapper">
 *       <button class="dismissable-container__button" />
 *     </div>
 *     {... Container Content ...}
 *   </div>
 * </div>
 *
 */
const applyDismissibleButton = ($domEl) => {
  // Make sure that the target element has the `data-dismissable`
  //  attribute set to `true`, otherwise just return.
  // This attribute "data-dismissable" is known to be misspelled. It is not simple to rename,
  //  so it is being left misspelled for compatibility.
  const isDismissible = $domEl.hasAttribute('data-dismissable');
  const id = $domEl.getAttribute('id');

  // If the container has no ID or is not dismissed, then leave the container as visible.
  if (!isDismissible || !id) {
    return;
  }

  // Check if current element has already been dismissed (is in session storage...)
  const dismissedContainers = sessionStorage.getItem('dismissedContainers')?.split(',') ?? [];

  // If the container is not dismissed, then fire analytics, if necessary,otherwise, trigger "hide"
  if (!dismissedContainers.includes(id)) {
    if (document.querySelector('.teaser.weather-advisory')) {
      utils.analytics.interaction(ANALYTICS.UI_BANNER, ANALYTICS.TRAVEL_ADVISORY, ANALYTICS.OPEN);
    }
  } else {
    hideContainer($domEl);
    return;
  }

  // Attach event listener to dismiss button
  const $dismissBtn = $domEl.querySelector('.js-dismiss');
  $dismissBtn?.addEventListener('click', (e) => {
    e.preventDefault();

    // prevent multiple clicks...
    $dismissBtn.blur();

    // update local storage
    const latestDismissedContainers = sessionStorage.getItem('dismissedContainers')?.split(',') ?? [];
    latestDismissedContainers.push(id);
    sessionStorage.setItem('dismissedContainers', latestDismissedContainers.join(','));

    hideContainer($domEl);
  });
};

// The widget code uses only `init` method from the exported object...
export default {
  init(store, $domEl) {
    applyBackgroundStyleToParent($domEl);
    applyDismissibleButton($domEl);
  },
};
